import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { UrlUtils } from '../../../shared/utils/url.utils';
import { ActivatedRoute, Router } from '@angular/router';
import { MediaService, SvgViewboxDirective } from '@exl-ng/mulo-core';

import { ProfileProjectsService } from '../profile-projects.service';
import { ProfileService } from '../../profile.service';
import { ProjectDetails } from '../../../shared/interfaces/project.interface';
import { PaginationInstance, NgxPaginationModule } from 'ngx-pagination';
import { ReplaySubject, finalize, takeUntil, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../../parts/dialogs/confirm-dialog/confirm-dialog.component';
import { Profile } from '../../../shared/configurations/profile.constant';
import { JwtUtilService } from '../../../core/jwt-util.service';
import { State } from '../../../shared/configurations/state.constant';
import { ConfigurationHandlerService } from '../../../core/configuration-handler.service';
import { ProjectService } from '../../../project/project.service';
import { PaginationUtils } from '../../../shared/utils/pagination.utils';
import { EspDateFormatPipe } from '../../../shared/pipes/date-format.pipe';
import { TranslateObjectPipe } from '../../../shared/pipes/translate-object.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { DiscoveryToolbarComponent } from '../../../discovery/discovery-toolbar/discovery-toolbar.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import {
    StatusTagComponent,
    ExpandableComponent,
    PagingComponent,
    HtmlSanitizePipe,
} from '@exl-ng/mulo-common';
import { CitationBriefComponent } from '../../../parts/citations/citation-brief/citation-brief.component';
import { CitationBriefPlaceholderComponent } from '../../../parts/citations/citation-brief-placeholder/citation-brief-placeholder.component';

@Component({
    selector: 'esp-profile-projects-group',
    templateUrl: './profile-projects-group.component.html',
    styleUrls: ['./profile-projects-group.component.scss'],
    imports: [
        CitationBriefPlaceholderComponent,
        CitationBriefComponent,
        StatusTagComponent,
        MatIcon,
        SvgViewboxDirective,
        MatTooltip,
        ExpandableComponent,
        DiscoveryToolbarComponent,
        NgxPaginationModule,
        PagingComponent,
        TranslateModule,
        TranslateObjectPipe,
        EspDateFormatPipe,
        HtmlSanitizePipe,
    ],
})
export class ProfileProjectsGroupComponent implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    profileProjectsService = inject(ProfileProjectsService);
    profileService = inject(ProfileService);
    private matDialog = inject(MatDialog);
    private jwtUtilService = inject(JwtUtilService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private projectService = inject(ProjectService);
    media = inject(MediaService);

    static readonly actionsMenu = [
        { name: 'research.projects.list.menu.view', action: 'view' },
        { name: 'research.projects.list.menu.edit', action: 'edit' },
        { name: 'research.projects.list.menu.delete', action: 'delete' },
        { name: 'research.profile.hide.action.hide', action: 'hide' },
        { name: 'research.profile.hide.action.display', action: 'show' },
    ];

    isLoading = false;
    institutionCode: string;
    _onDestroy = new ReplaySubject<void>();

    projects: ProjectDetails[] = [];
    facet: string;
    pagesCount: number;

    paginationConfig: PaginationInstance = {
        id: 'serverPagingOutput',
        itemsPerPage: Profile.BULK_SIZE,
        currentPage: 1,
    };

    trackByProject = (index: number, project: ProjectDetails) => project.id;

    ngOnInit(): void {
        this.institutionCode = UrlUtils.getParam('institution');
        let page = '';
        if (this.route.snapshot.queryParamMap) {
            page = this.route.snapshot.queryParamMap.get('page');
        }
        this.route.data.subscribe((data) => {
            if (page) {
                this.paginationConfig.currentPage = page as unknown as number;
            } else {
                this.paginationConfig.currentPage = 1;
            }
            page = '';
            this.facet = data.resourceType as unknown as string;
            this.setPaginationTotalItems();

            if (this.profileProjectsService.facets.length) {
                this.getProjectsList();
            }
        });

        this.profileProjectsService.facetsChange$
            .pipe(takeUntil(this._onDestroy))
            .subscribe(() => {
                this.getProjectsList();
            });
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    getProjectsList() {
        this.isLoading = true;
        this.projects = [];
        this.profileProjectsService
            .getAllProjects(this.facet, this.searchOffset())
            .pipe(
                takeUntil(this._onDestroy),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe((data) => {
                this.projects = data.results;
                this.setPaginationTotalItems();
                this.pagesCount = PaginationUtils.calcPagesCount(
                    this.paginationConfig,
                );
            });
    }

    getMenuAction(project: ProjectDetails) {
        let menu = ProfileProjectsGroupComponent.actionsMenu;
        if (this.profileService.isPublicPage) {
            return [];
        }
        if (
            !this.isResearcherProjectCreator(project.researcherProjectCreatorId)
        ) {
            menu = menu.filter((item) => item.action !== 'delete');
        }
        if (
            !this.configurationHandlerService.configurationByDefualtView()
                .headerFooter?.addProjects ||
            this.media.is('lt-md')
        ) {
            menu = menu.filter((item) => item.action !== 'edit');
        }
        if (project.visibleInCurrentUserPublicProfile) {
            menu = menu.filter((item) => item.action !== 'show');
        } else {
            menu = menu.filter((item) => item.action !== 'hide');
        }
        return menu;
    }

    isResearcherProjectCreator(researcherProjectCreatorId: string) {
        return researcherProjectCreatorId === this.jwtUtilService.getUserID();
    }

    onActionClick(action, project: ProjectDetails, index) {
        switch (action) {
            case 'research.projects.list.menu.delete':
                this.showProjecttDeleteConfirmationDialog(project, index);
                break;
            case 'research.projects.list.menu.edit':
                this.navigateToProjectPage(project, true);
                break;
            case 'research.projects.list.menu.view':
                this.navigateToProjectPage(project);
                break;
            case 'research.profile.hide.action.hide':
                this.showProjectHideConfirmationDialog(project);
                break;
            case 'research.profile.hide.action.display':
                this.updateVisibleInProfile(project, true);
                break;
        }
    }

    showProjecttDeleteConfirmationDialog(item, index) {
        const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: 'research.projects.list.menu.delete.confirmation.title',
                message:
                    'research.projects.list.menu.delete.confirmation.message',
                actions: {
                    cancel: 'research.projects.list.menu.delete.confirmation.cancel',
                    confirm:
                        'research.projects.list.menu.delete.confirmation.confirm',
                },
            },
        });
        dialogRef.afterClosed().subscribe((response) => {
            if (response === 'ok') {
                this.deleteProject(item, index);
            }
        });
    }

    showProjectHideConfirmationDialog(item) {
        const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: 'research.profile.hide.confirmation.title',
                message: 'research.profile.hide.confirmation.message',
                actions: {
                    cancel: 'research.profile.hide.confirmation.cancel',
                    confirm: 'research.profile.hide.confirmation.confirm',
                },
            },
        });
        dialogRef.afterClosed().subscribe((response) => {
            if (response === 'ok') {
                this.updateVisibleInProfile(item, false);
            }
        });
    }

    navigateToProjectPage(project: ProjectDetails, isEdit = false) {
        const queryParams = { institution: this.institutionCode };
        if (isEdit) {
            queryParams['mode'] = 'edit';
        }
        this.router.navigate([project.projectUrl], { queryParams });
    }

    deleteProject(item, index) {
        this.profileProjectsService.allProjectsFacetsCount--;
        this.profileProjectsService.deleteProject(item).subscribe(() => {
            setTimeout(() => {
                if (this.profileProjectsService.allProjectsFacetsCount > 0) {
                    this.profileProjectsService.getFacetsProjects();
                } else {
                    this.profileService.sections =
                        this.profileService.sections.filter(
                            (section) => section.path !== 'projects',
                        );
                    this.router.navigate([State.PROFILE], {
                        queryParams: { institution: this.institutionCode },
                    });
                }
            }, 2500);
        });
    }

    setPaginationTotalItems() {
        this.paginationConfig.totalItems =
            this.profileProjectsService.facets.find(
                (facet) => facet.value === this.facet,
            )?.count || 0;
    }

    searchOffset() {
        return (this.paginationConfig.currentPage - 1) * Profile.BULK_SIZE;
    }

    pageChange(event) {
        this.paginationConfig.currentPage = event;
        this.projects = [];
        this.isLoading = true;
        this.getProjectsList();
        this.addPageParamToURL();
    }

    addPageParamToURL() {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { page: this.paginationConfig.currentPage },
            queryParamsHandling: 'merge',
        });
    }

    getNumOfCopies() {
        return PaginationUtils.getNumOfCopies(this.paginationConfig);
    }

    updateVisibleInProfile(project: ProjectDetails, setTo: boolean) {
        project.visibleInCurrentUserPublicProfile = setTo;
        this.projectService.loading = true;
        this.projectService.projectId = project.id;
        this.projectService
            .updatePublicProfileVisibility(setTo)
            .subscribe((response: ProjectDetails) => {
                project = response;
                this.projectService.loading = false;
            });
    }

    organizationsCodes(project: ProjectDetails) {
        return project.extras?.owningDepartments &&
            Object.keys(project.extras.owningDepartments).length
            ? Object.values(project.extras.owningDepartments)
            : [];
    }
}
